import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { Observable } from 'rxjs';
import { delay, map } from 'rxjs/operators';

import { CopyService } from '@amfam/shared/utility/shared-services';

import * as appStateActions from '../../+state/app-state/app-state.actions';
import { APP_STATE, MAP_TRIGGER } from '../../+state/app-state/app-state.models';
import { SetConfigAction } from '../../+state/config/config.actions';
import { DrpSearchConfig } from '../../+state/config/config.models';
import * as geoCoderActions from '../../+state/geo-coder/geo-coder.actions';
import { FullAddressDetails } from '../../+state/geo-coder/geo-coder.models';
import * as paginationActions from '../../+state/pagination/pagination.actions';
import * as fromRoot from '../../+state/selectors';
import { State } from '../../+state/selectors';
import * as ServiceLocatorActions from '../../+state/service-locator/service-locator.actions';
import {
  SEARCH_TYPES,
  SearchContext,
  SearchedValue,
  SearchResult
} from '../../+state/service-locator/service-locator.models';
import { DrpAnalyticsConstants } from '../../services/drp-search-constants';
import { LoadingSpinnerService } from '../../services/loading-spinner.service';

@Component({
  selector: 'drp-search',
  templateUrl: './drp-search.component.html',
  styleUrls: ['./drp-search.component.scss'],
  standalone: false
})
export class DrpSearchComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() config: DrpSearchConfig;

  @Input() searchContext: SearchContext;

  @Input() initalSearchValue: SearchedValue;

  @Input() isPartner = false;
  @Output() shopSelected = new EventEmitter<any>();

  @Output() cancel = new EventEmitter<void>();

  @Output() drpSearchAnalytic = new EventEmitter<DrpAnalyticsConstants>();

  APP_STATE_ENUM: any;

  SEARCH_CONTEXT = SearchContext; // Used on template to check for specific search context

  SEARCH_TYPES = SEARCH_TYPES;

  RESULTS_OPTIONS = DrpAnalyticsConstants;

  appState: Observable<APP_STATE>; // TODO: not sure if I need
  searchType: Observable<SEARCH_TYPES>;
  shopSearchResults: Observable<SearchResult[]>;
  searchedValue: Observable<SearchedValue>;
  isAppLoading: Observable<any>;
  searching: Observable<boolean>;
  currentMapRedrawTriggerState: Observable<MAP_TRIGGER>;
  getTotalPages: Observable<number>;
  shopResultsPageItems: Observable<any[]>;
  selectedShop: Observable<SearchResult>;
  errors: Observable<any>;
  lastSuccessfulSearchedValue: Observable<SearchedValue>;
  activeSearchType: SEARCH_TYPES;
  amfamPhone: string = this.copyService.getCopy('shared.amfamTelNumber');

  constructor(
    private store: Store<State>,
    private spinner: LoadingSpinnerService,
    private copyService: CopyService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    // Set the config values, reset the app when they change
    if (_get(changes, 'config.currentValue')) {
      this.store.dispatch(new appStateActions.InitAppStateAction());
      this.store.dispatch(new SetConfigAction(changes.config.currentValue));
    }

    // Set the search context
    if (_get(changes, 'searchContext.currentValue')) {
      this.store.dispatch(
        new ServiceLocatorActions.ServiceSearchInitAction({
          searchContext: this.searchContext
        })
      );
    }

    // fire the initial search as long as we have a searchContext
    if (_get(changes, 'initalSearchValue.currentValue') && this.searchContext) {
      this.store.dispatch(new ServiceLocatorActions.ServiceSearchAction(this.initalSearchValue));
    }
  }

  ngOnInit() {
    this.APP_STATE_ENUM = APP_STATE;
    this.spinner.start();
    this.appState = this.store.select(fromRoot.getCurrentAppState);
    this.store.dispatch(
      new ServiceLocatorActions.ServiceSearchInitAction({
        searchContext: this.searchContext
      })
    );
    if (this.initalSearchValue) {
      this.store.dispatch(new ServiceLocatorActions.ServiceSearchAction(this.initalSearchValue));
    }

    // TODO: Makre this the right object in the store instead of having to map from string to object here. Also in searchwrapper.
    this.searchType = this.store.select(fromRoot.getSearchType).pipe(
      map(searchTypeString => {
        switch (searchTypeString) {
          case 'autocomplete':
            this.activeSearchType = SEARCH_TYPES.COORDINATES;
            return SEARCH_TYPES.COORDINATES;
          case 'location':
            this.activeSearchType = SEARCH_TYPES.LOCATION;
            return SEARCH_TYPES.LOCATION;
          case 'name':
            this.activeSearchType = SEARCH_TYPES.NAME;
            return SEARCH_TYPES.NAME;
          default:
            this.activeSearchType = SEARCH_TYPES.ERROR;
            return SEARCH_TYPES.ERROR;
        }
      })
    );

    this.shopSearchResults = this.store.select(fromRoot.getSearchResults);
    this.searchedValue = this.store.select(fromRoot.getSearchedValue);
    // TODO fix pipe(delay(0))
    this.shopResultsPageItems = this.store.select(fromRoot.getPageItems).pipe(delay(0));
    this.getTotalPages = this.store.select(fromRoot.getTotalPages);
    this.isAppLoading = this.store.select(fromRoot.getLoading);
    this.selectedShop = this.store.select(fromRoot.getSelectedShop);
    // TODO fix pipe(delay(0))
    this.searching = this.store.select(fromRoot.getDrpLoading).pipe(delay(0));
    this.currentMapRedrawTriggerState = this.store.select(fromRoot.getMapRedrawTriggerState);
    this.lastSuccessfulSearchedValue = this.store.select(fromRoot.getLastSuccessfulSearchedValue);
    this.errors = this.store.select(fromRoot.getErrors);
    this.appState.subscribe(state => {
      if (
        state === this.APP_STATE_ENUM.RESULTS_NOT_FOUND ||
        state === this.APP_STATE_ENUM.NO_MAP_RESULTS
      ) {
        if (this.searchContext === SearchContext.DRP_SHOP_SEARCH) {
          this.analyticEvent(this.RESULTS_OPTIONS.DRP_SEARCH_NO_RESULTS_FOUND);
        } else if (this.searchContext === SearchContext.DRP_SHOP_SELECTION) {
          this.analyticEvent(this.RESULTS_OPTIONS.DRP_NO_RESULTS_FOUND);
        } else if (this.searchContext === SearchContext.RENTAL_SELECTION) {
          this.analyticEvent(this.RESULTS_OPTIONS.RENTAL_NO_RESULTS_FOUND);
        }
      }
    });
    this.errors.subscribe(err => {
      if (err.length > 0) {
        const statusCodes = err.map(e => e.status);
        if (statusCodes.includes(500) || statusCodes.includes(502)) {
          if (this.searchContext === SearchContext.DRP_SHOP_SEARCH) {
            this.analyticEvent(this.RESULTS_OPTIONS.DRP_SEARCH_ERROR);
          }
        }
      }
    });
    // TODO: need to fix loading spinner on page load
    // TODO fix pipe(delay(0))
    this.isAppLoading.pipe(delay(0)).subscribe(val => {
      if (val) {
        this.spinner.start();
      } else {
        this.spinner.stop();
      }
    });
  }

  ngAfterViewInit() {
    setTimeout(_ => this.spinner.stop(), 500);
  }
  /**
   * When user drags the search location indicator, use the zipcode and currently set
   * search distance to update the search results on the page
   */
  searchWithNewCoordinates(searchedValue: SearchedValue) {
    searchedValue.distance = '15';
    this.store.dispatch(new ServiceLocatorActions.ServiceSearchAction(searchedValue));
  }

  updateFormZipCode(zipCode: string) {
    const update: FullAddressDetails = {
      formatted_address: '',
      address_line1: '',
      city: '',
      stateCode: '',
      Country: '',
      zip: zipCode
    };
    this.store.dispatch(new geoCoderActions.SetFullAddressAction(update));
  }

  /**
   * Set the Google State's "selectedShop" to the search marker that the user selected
   */
  setShopSelection(result: SearchResult) {
    this.store.dispatch(new geoCoderActions.SetSelectedShopAction(result));
  }
  /**
   * Dispatch new action
   */
  setAppState(state: string) {
    this.store.dispatch(new appStateActions.SetAppStateAction(state));
  }

  /**
   * Set up the pagination values in the store
   */

  initializePagination(totalItems: any) {
    if (this.searchContext === SearchContext.DRP_SHOP_SELECTION) {
      this.analyticEvent(this.RESULTS_OPTIONS.DRP_RESULTS_FOUND);
    } else if (this.searchContext === SearchContext.RENTAL_SELECTION) {
      this.analyticEvent(this.RESULTS_OPTIONS.RENTAL_RESULTS_FOUND);
    } else if (this.searchContext === SearchContext.DRP_SHOP_SEARCH) {
      if (this.activeSearchType === SEARCH_TYPES.LOCATION) {
        this.analyticEvent(this.RESULTS_OPTIONS.DRP_SEARCH_LOCATION_RESULTS_FOUND);
      } else if (this.activeSearchType === SEARCH_TYPES.NAME) {
        this.analyticEvent(this.RESULTS_OPTIONS.DRP_SEARCH_NAME_RESULTS_FOUND);
      }
    }

    this.store.dispatch(new paginationActions.InitializePaginationAction(totalItems));
  }

  /**
   * Set current page number in pagination
   */
  setCurrentPage(page: any) {
    this.store.dispatch(new paginationActions.SetCurrentPageAction(page));
  }

  analyticEvent(results: DrpAnalyticsConstants) {
    this.drpSearchAnalytic.emit(results);
  }

  /**
   *
   */
  handleMapEvent(mapEvent: any) {
    this.store.dispatch(new appStateActions.SetMapRedrawTriggerAction(mapEvent));
  }

  /**
   * Store last successful searched value to revert back to if there are any errors
   */
  setLastSuccessfulSearchedValue(value: any) {
    this.store.dispatch(new ServiceLocatorActions.SetLastSuccessfulSearchedValueAction(value));
  }
}
